import React from "react";
import PropTypes from "prop-types";
import { Box, useTheme } from "@mui/material";
import {
  CREDIT_DOTTED_SEGMENT_COUNT,
  CREDIT_USAGE_STATUS,
  getCreditDottedSegmentCounts,
  getCreditUsageStatusColor,
} from "../../helper/creditBalanceModel";

/**
 * Compact segmented credits indicator — filled dots = remaining allowance.
 */
export default function CreditsDottedProgress({
  remainingRatio = 0,
  status = CREDIT_USAGE_STATUS.HEALTHY,
  segmentCount = CREDIT_DOTTED_SEGMENT_COUNT,
  layout = "spread",
}) {
  const theme = useTheme();
  const { filled, segmentCount: total } = getCreditDottedSegmentCounts(
    remainingRatio,
    segmentCount
  );
  const remainingPercent = Math.round(
    Math.max(0, Math.min(1, Number(remainingRatio) || 0)) * 100
  );
  const activeColor = getCreditUsageStatusColor(theme.palette, status);
  const inactiveColor =
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.12)"
      : "rgba(88, 89, 91, 0.14)";

  return (
    <Box
      data-testid="credits-dotted-progress"
      role="progressbar"
      aria-valuenow={remainingPercent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Credits remaining"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: layout === "cluster" ? "flex-start" : "space-between",
        gap: layout === "cluster" ? 0.625 : 0,
        width: "100%",
        minWidth: 0,
      }}
    >
      {Array.from({ length: total }, (_, index) => (
        <Box
          key={index}
          component="span"
          aria-hidden
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            flexShrink: 0,
            bgcolor: index < filled ? activeColor : inactiveColor,
          }}
        />
      ))}
    </Box>
  );
}

CreditsDottedProgress.propTypes = {
  remainingRatio: PropTypes.number,
  status: PropTypes.string,
  segmentCount: PropTypes.number,
  layout: PropTypes.oneOf(["spread", "cluster"]),
};
